import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../../../supabaseClient";
import { getPostLoginRoute, type AppUser } from "../../../auth/AuthProvider";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type LocationState = {
  from?: {
    pathname?: string;
  };
};

const cardStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 460,
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 20,
  boxShadow: "0 24px 60px rgba(15, 23, 42, 0.08)",
  padding: "2rem",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  marginBottom: "0.45rem",
  fontSize: "0.95rem",
  fontWeight: 600,
  color: "#0f172a",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.9rem 1rem",
  borderRadius: 12,
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  color: "#0f172a",
  fontSize: "0.95rem",
  outline: "none",
  boxSizing: "border-box",
};

const errorTextStyle: React.CSSProperties = {
  color: "#b91c1c",
  fontSize: "0.82rem",
  marginTop: "0.45rem",
};

const dividerTextStyle: React.CSSProperties = {
  position: "relative",
  textAlign: "center",
  margin: "1.2rem 0",
  fontSize: "0.85rem",
  color: "#64748b",
};

const getSafeRedirectPath = (pathname?: string) => {
  if (!pathname) return null;
  if (!pathname.startsWith("/")) return null;
  if (pathname.startsWith("//")) return null;
  if (pathname === "/login") return null;
  if (pathname === "/signup") return null;
  if (pathname === "/auth/callback") return null;
  return pathname;
};

const mapSessionUserToAppShape = (sessionUser: any): AppUser => {
  const metadata = sessionUser?.user_metadata ?? {};
  const appMetadata = sessionUser?.app_metadata ?? {};

  const rawRole =
    metadata.role ??
    appMetadata.role ??
    metadata.userRole ??
    appMetadata.userRole ??
    null;

  const normalizedRole =
    typeof rawRole === "string"
      ? rawRole.trim().toLowerCase() === "spowner"
        ? "provider_owner"
        : rawRole.trim().toLowerCase() === "hnbstaff"
          ? "hnb_staff"
          : rawRole.trim().toLowerCase()
      : null;

  return {
    id: sessionUser?.id ?? "",
    name: metadata.full_name || sessionUser?.email || "User",
    email: sessionUser?.email || "",
    role:
      normalizedRole === "customer" ||
      normalizedRole === "provider_owner" ||
      normalizedRole === "stylist" ||
      normalizedRole === "hnb_staff"
        ? normalizedRole
        : null,
    providerStatus:
      typeof metadata.providerStatus === "string"
        ? metadata.providerStatus
        : typeof appMetadata.providerStatus === "string"
          ? appMetadata.providerStatus
          : undefined,
    providerOnboardingCompleted:
      typeof metadata.providerOnboardingCompleted === "boolean"
        ? metadata.providerOnboardingCompleted
        : typeof appMetadata.providerOnboardingCompleted === "boolean"
          ? appMetadata.providerOnboardingCompleted
          : false,
    providerOnboardingStep:
      typeof metadata.providerOnboardingStep === "string"
        ? metadata.providerOnboardingStep
        : typeof appMetadata.providerOnboardingStep === "string"
          ? appMetadata.providerOnboardingStep
          : undefined,
  };
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const from = useMemo(() => {
    const state = location.state as LocationState | null;
    return getSafeRedirectPath(state?.from?.pathname);
  }, [location.state]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    email?: string;
    password?: string;
  }>({});
  const [submitting, setSubmitting] = useState(false);
  const [googleSubmitting, setGoogleSubmitting] = useState(false);

  const validate = () => {
    const errors: { email?: string; password?: string } = {};

    if (!email.trim()) {
      errors.email = "Email is required.";
    } else if (!EMAIL_REGEX.test(email.trim())) {
      errors.email = "Enter a valid email address.";
    }

    if (!password) {
      errors.password = "Password is required.";
    } else if (password.length < 6) {
      errors.password = "Password must be at least 6 characters.";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (!validate()) return;

    try {
      setSubmitting(true);

      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (error) {
        setFormError(error.message);
        return;
      }

      const nextUser = data.session?.user
        ? mapSessionUserToAppShape(data.session.user)
        : null;

      navigate(from || getPostLoginRoute(nextUser), { replace: true });
    } catch (err) {
      console.error("Login error", err);
      setFormError("Something went wrong. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleAuth = async () => {
    try {
      setFormError(null);
      setGoogleSubmitting(true);

      const callbackRedirect = from
        ? `${window.location.origin}/auth/callback?redirect=${encodeURIComponent(from)}`
        : `${window.location.origin}/auth/callback`;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: callbackRedirect,
        },
      });

      if (error) {
        setFormError(error.message);
      }
    } catch (err) {
      console.error("Google auth error", err);
      setFormError("Google sign-in could not be started. Try again in a moment.");
    } finally {
      setGoogleSubmitting(false);
    }
  };

  return (
    <main
      style={{
        minHeight: "calc(100vh - 160px)",
        background:
          "linear-gradient(180deg, #f8fafc 0%, #ffffff 40%, #f8fafc 100%)",
        padding: "3rem 1.25rem 4rem",
      }}
    >
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "2rem",
          alignItems: "center",
        }}
      >
        <section>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.45rem 0.8rem",
              borderRadius: 999,
              background: "#e2e8f0",
              color: "#334155",
              fontSize: "0.82rem",
              fontWeight: 700,
              letterSpacing: "0.01em",
              marginBottom: "1rem",
            }}
          >
            HnB access
          </div>

          <h1
            style={{
              margin: 0,
              fontSize: "clamp(2rem, 4vw, 3.2rem)",
              lineHeight: 1.05,
              letterSpacing: "-0.03em",
              color: "#0f172a",
            }}
          >
            Sign in to continue with HnB
          </h1>

          <p
            style={{
              marginTop: "1rem",
              maxWidth: 560,
              fontSize: "1.02rem",
              lineHeight: 1.75,
              color: "#475569",
            }}
          >
            Access your account to continue with bookings, saved activity, and
            protected platform workflows. Public browsing, provider discovery,
            and plan information remain available outside sign-in.
          </p>

          <div
            style={{
              marginTop: "1.5rem",
              display: "grid",
              gap: "0.9rem",
              color: "#334155",
              fontSize: "0.95rem",
            }}
          >
            <div>• Continue to your correct dashboard after successful login.</div>
            <div>• Use public pages to explore providers and platform plans first.</div>
            <div>• Keep provider, customer, and internal access separated by route.</div>
          </div>
        </section>

        <section>
          <div style={cardStyle}>
            <h2
              style={{
                margin: 0,
                fontSize: "1.5rem",
                color: "#0f172a",
                letterSpacing: "-0.02em",
              }}
            >
              Login
            </h2>

            <p
              style={{
                marginTop: "0.6rem",
                marginBottom: "1.5rem",
                color: "#64748b",
                lineHeight: 1.6,
                fontSize: "0.95rem",
              }}
            >
              Enter your account details to continue.
            </p>

            <button
              type="button"
              onClick={handleGoogleAuth}
              disabled={googleSubmitting || submitting}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.75rem",
                padding: "0.9rem 1rem",
                borderRadius: 999,
                border: "1px solid #d1d5db",
                background: "#ffffff",
                color: "#111827",
                fontSize: "0.95rem",
                fontWeight: 700,
                cursor: googleSubmitting || submitting ? "not-allowed" : "pointer",
                marginBottom: "1rem",
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#f3f4f6",
                  fontSize: "0.9rem",
                  fontWeight: 800,
                }}
              >
                G
              </span>
              {googleSubmitting ? "Redirecting to Google..." : "Continue with Google"}
            </button>

            <div style={dividerTextStyle}>
              <span
                style={{
                  background: "#ffffff",
                  padding: "0 0.75rem",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                or continue with email
              </span>
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: 0,
                  right: 0,
                  height: 1,
                  background: "#e5e7eb",
                  transform: "translateY(-50%)",
                }}
              />
            </div>

            <form onSubmit={handleSubmit} noValidate>
              <div style={{ marginBottom: "1rem" }}>
                <label htmlFor="email" style={labelStyle}>
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (formError) setFormError(null);
                    if (fieldErrors.email) {
                      setFieldErrors((prev) => ({ ...prev, email: undefined }));
                    }
                  }}
                  style={inputStyle}
                  aria-invalid={fieldErrors.email ? "true" : "false"}
                />
                {fieldErrors.email && <p style={errorTextStyle}>{fieldErrors.email}</p>}
              </div>

              <div style={{ marginBottom: "0.35rem" }}>
                <label htmlFor="password" style={labelStyle}>
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (formError) setFormError(null);
                    if (fieldErrors.password) {
                      setFieldErrors((prev) => ({ ...prev, password: undefined }));
                    }
                  }}
                  style={inputStyle}
                  aria-invalid={fieldErrors.password ? "true" : "false"}
                />
                {fieldErrors.password && (
                  <p style={errorTextStyle}>{fieldErrors.password}</p>
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginBottom: "1rem",
                }}
              >
                <Link
                  to="/forgot-password"
                  style={{
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    color: "#0f172a",
                    textDecoration: "none",
                  }}
                >
                  Forgot password?
                </Link>
              </div>

              {formError && (
                <div
                  style={{
                    marginBottom: "1rem",
                    padding: "0.85rem 1rem",
                    borderRadius: 12,
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    color: "#991b1b",
                    fontSize: "0.92rem",
                    lineHeight: 1.6,
                  }}
                >
                  {formError}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || googleSubmitting}
                style={{
                  width: "100%",
                  border: "none",
                  borderRadius: 999,
                  padding: "0.95rem 1rem",
                  background: submitting ? "#475569" : "#111827",
                  color: "#ffffff",
                  fontSize: "0.96rem",
                  fontWeight: 700,
                  cursor: submitting || googleSubmitting ? "not-allowed" : "pointer",
                  boxShadow: "0 14px 30px rgba(15, 23, 42, 0.14)",
                }}
              >
                {submitting ? "Logging in..." : "Login"}
              </button>
            </form>

            <p
              style={{
                marginTop: "1rem",
                marginBottom: 0,
                fontSize: "0.92rem",
                color: "#64748b",
                lineHeight: 1.6,
              }}
            >
              New here?{" "}
              <Link
                to="/signup"
                style={{
                  color: "#0f172a",
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                Create an account
              </Link>
            </p>
          </div>
        </section>
      </div>
    </main>
  );
};

export default LoginPage;